import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Header from './components/Header';
import TabBar from './components/TabBar';
import Sidebar from './components/Sidebar';
import DesktopTopBar from './components/DesktopTopBar';
import Login from './components/Login';
import TradeView from './components/views/TradeView';
import PositionsView from './components/views/PositionsView';
import WatchlistView from './components/views/WatchlistView';
import AnalyticsView from './components/views/AnalyticsView';
import SettingsView from './components/views/SettingsView';
import { BotProvider, useBot } from './context/BotContext';
import { useAuth } from './context/AuthContext';
import { DEFAULT_TAB } from './constants/tabs';

const { FiInfo, FiCheckCircle, FiAlertTriangle, FiLoader } = FiIcons;

function ToastNotifications() {
  const { notifications } = useBot();
  return (
    <div className="fixed top-16 lg:top-10 left-0 lg:left-56 right-0 z-50 flex flex-col items-center gap-2 pointer-events-none px-4">
      <AnimatePresence>
        {notifications.map(note => (
          <motion.div
            key={note.id}
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`w-full max-w-sm px-4 py-3 rounded-xl shadow-lg border flex items-center gap-3 ${
              note.type === 'error'   ? 'bg-red-500/10 border-red-500/30 text-red-400' :
              note.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' :
                                        'bg-blue-500/10 border-blue-500/30 text-blue-400'
            }`}
          >
            <SafeIcon
              icon={note.type === 'error' ? FiAlertTriangle : note.type === 'success' ? FiCheckCircle : FiInfo}
              className="text-lg flex-shrink-0"
            />
            <p className="text-sm font-medium leading-tight">{note.message}</p>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function BotApplication() {
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);

  return (
    <div className="min-h-screen bg-[#000000] lg:bg-[#0a0a0b] lg:flex font-sans">

      {/* Desktop sidebar — hidden on mobile via its own hidden lg:flex class */}
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Main column: centered narrow card on mobile, flex-1 on desktop */}
      <div className="max-w-md mx-auto w-full lg:max-w-none lg:mx-0 lg:flex-1 bg-[#0a0a0b] min-h-screen sm:border-x sm:border-[#2c2c2e] lg:border-0 shadow-2xl shadow-black/50 lg:shadow-none overflow-hidden flex flex-col relative">

        {/* Mobile header — hidden on desktop */}
        <div className="lg:hidden">
          <Header />
        </div>

        {/* Desktop slim top bar — hidden on mobile */}
        <div className="hidden lg:block">
          <DesktopTopBar activeTab={activeTab} />
        </div>

        <ToastNotifications />

        <main className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide pb-16 lg:pb-0">
          {activeTab === 'trade'     && <TradeView />}
          {activeTab === 'positions' && <PositionsView />}
          {activeTab === 'watchlist' && <WatchlistView />}
          {activeTab === 'analytics' && <AnalyticsView />}
          {activeTab === 'settings'  && <SettingsView />}
        </main>

        {/* Mobile tab bar — hidden on desktop */}
        <div className="lg:hidden">
          <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </div>
    </div>
  );
}

function AuthedApp() {
  return (
    <BotProvider>
      <BotApplication />
    </BotProvider>
  );
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <SafeIcon icon={FiLoader} className="text-3xl text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!user) return <Login />;

  return <AuthedApp />;
}
